import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../../utils/api';
import { useToast, ToastHost } from '../../../components/portal/Toast';
import AttendanceCalendar from '../../../components/portal/AttendanceCalendar';
import AttendanceDayModal from '../../../components/portal/AttendanceDayModal';
import { RiUserLine } from 'react-icons/ri';

export default function AdminAttendance() {
  const today = useMemo(() => new Date(), []);
  const { toast, clearToast } = useToast();

  // Calendar period
  const [calMonth, setCalMonth] = useState(today.getMonth() + 1);
  const [calYear, setCalYear] = useState(today.getFullYear());

  // Employee filter — empty string = all employees
  const [filterUserId, setFilterUserId] = useState('');
  const [employees, setEmployees] = useState([]);

  // Data
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  // Day modal
  const [openDay, setOpenDay] = useState(null);

  // Load employees once
  useEffect(() => {
    api.get('/users/employees')
      .then((res) => {
        const d = res.data;
        const list = d?.users || d?.employees || d?.data || (Array.isArray(d) ? d : []);
        setEmployees(list);
      })
      .catch(() => {});
  }, []);

  // Load attendance for current month + selected employee.
  // No employee selected → don't hit the API; show an empty prompt instead.
  const loadMonth = useCallback(() => {
    if (!filterUserId) {
      setEntries([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const params = { month: calMonth, year: calYear, limit: 1000, user_id: filterUserId };
    api.get('/attendance', { params })
      .then((res) => {
        const d = res.data;
        const list = Array.isArray(d) ? d : d?.trackers || d?.data || [];
        // Admin endpoint returns either:
        //   [{ user, dates: [{ date, items:[] }] }]  (per-user grouped)
        //   [{ date, items:[] }]                      (cross-user grouped)
        // Flatten both shapes into a single array of items with date + user.
        const flat = [];
        for (const node of list) {
          if (Array.isArray(node.dates)) {
            for (const day of node.dates) {
              for (const it of (day.items || day.trackers || [])) {
                flat.push({
                  ...it,
                  date: day.date || it.date,
                  user: it.user || node.user || null,
                  user_id: it.user_id || node.user?.id || node.user?._id || node.user_id,
                });
              }
            }
          } else if (Array.isArray(node.items) || Array.isArray(node.trackers)) {
            for (const it of (node.items || node.trackers)) {
              flat.push({
                ...it,
                date: node.date || it.date,
                user: it.user || null,
                user_id: it.user_id || it.user?.id || it.user?._id,
              });
            }
          } else {
            // Already a flat item
            flat.push(node);
          }
        }
        setEntries(flat);
      })
      .catch(() => setEntries([]))
      .finally(() => setLoading(false));
  }, [calMonth, calYear, filterUserId]);

  useEffect(() => { loadMonth(); }, [loadMonth]);

  // Pre-compute monthly stats from entries (for the header)
  const monthStats = useMemo(() => {
    let totalMs = 0;
    let working = 0;
    const userIds = new Set();
    for (const it of entries) {
      if (!it.start_time) continue;
      const start = new Date(it.start_time).getTime();
      const end = it.end_time ? new Date(it.end_time).getTime() : Date.now();
      totalMs += Math.max(0, end - start);
      if (!it.end_time) working++;
      const uid = it.user_id || it.user?.id || it.user?._id;
      if (uid) userIds.add(uid);
    }
    return { totalMs, working, users: userIds.size, count: entries.length };
  }, [entries]);

  return (
    <div className="p-7 space-y-6 bg-[#F8FAFC]">
      <ToastHost toast={toast} clearToast={clearToast} />

      {/* Header */}
      <div>
        <p className="label-meta mb-1">Team</p>
        <h1 className="heading-md text-[#1F2937]">Attendance Calendar</h1>
        <p className="text-[#6B7280] text-sm mt-1">
          Browse every employee's tracked time on a calendar. Filter by employee, then click any day for details.
        </p>
      </div>

      {/* Filter bar */}
      <div className="bg-white border border-[#E5E7EB] rounded-2xl shadow-[0_4px_20px_rgba(15,23,42,0.06)] p-5">
        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto_auto] gap-3 items-end">
          {/* Employee dropdown */}
          <div>
            <label className="block text-[10px] font-semibold text-[#9CA3AF] uppercase tracking-wide mb-1.5 flex items-center gap-1">
              <RiUserLine size={11} /> Employee
            </label>
            <select
              value={filterUserId}
              onChange={(e) => setFilterUserId(e.target.value)}
              className="input-field cursor-pointer text-sm w-full"
            >
              <option value="">— Select an employee —</option>
              {employees.map((u) => {
                const id = u.id || u._id;
                return (
                  <option key={id} value={id}>
                    {u.name || u.username || u.email_id}
                  </option>
                );
              })}
            </select>
          </div>

          {/* Stat tiles — only meaningful once an employee is picked */}
          {filterUserId && (
            <>
              <StatPill label="Entries" value={loading ? '—' : monthStats.count} color="#7c3aed" />
              <StatPill label="Working" value={loading ? '—' : monthStats.working} color="#f59e0b" />
            </>
          )}
        </div>
        {filterUserId && (
          <p className="text-[#94A3B8] text-xs mt-3">
            Viewing attendance for{' '}
            <span className="font-semibold text-[#0F172A]">
              {(() => {
                const u = employees.find((x) => (x.id || x._id) === filterUserId);
                return u?.name || u?.username || u?.email_id || 'employee';
              })()}
            </span>
            .{' '}
            <button
              onClick={() => setFilterUserId('')}
              className="text-[#0984e3] font-semibold hover:underline cursor-pointer"
            >
              Change
            </button>
          </p>
        )}
      </div>

      {/* Empty prompt when no employee is selected */}
      {!filterUserId ? (
        <div className="bg-white border border-[#E5E7EB] rounded-2xl shadow-[0_4px_20px_rgba(15,23,42,0.06)] p-12 text-center">
          <div
            className="w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center text-white"
            style={{ background: 'linear-gradient(135deg,#0984e3 0%,#7c3aed 100%)' }}
          >
            <RiUserLine size={28} />
          </div>
          <h3 className="text-[#0F172A] font-bold text-lg">Select an employee to begin</h3>
          <p className="text-[#6B7280] text-sm mt-1 max-w-md mx-auto">
            Pick a name from the dropdown above to view that employee's attendance calendar.
          </p>
        </div>
      ) : (
        <>
          {/* Calendar */}
          <AttendanceCalendar
            month={calMonth}
            year={calYear}
            onMonthChange={({ month, year }) => { setCalMonth(month); setCalYear(year); }}
            entries={entries}
            loading={loading}
            showUserSummary={false}
            onDayClick={(dateStr, items) => setOpenDay({ dateStr, items })}
          />

          {/* Day-details modal */}
          {openDay && (
            <AttendanceDayModal
              dateStr={openDay.dateStr}
              items={openDay.items}
              onClose={() => setOpenDay(null)}
              showUser
              groupByUser={false}
            />
          )}
        </>
      )}
    </div>
  );
}

function StatPill({ label, value, color }) {
  return (
    <div
      className="rounded-xl px-4 py-2.5 min-w-[110px]"
      style={{ background: `${color}10`, border: `1px solid ${color}30` }}
    >
      <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color }}>{label}</p>
      <p className="text-[#0F172A] font-bold tabular-nums" style={{ fontSize: 16 }}>{value}</p>
    </div>
  );
}
